/* 
  Custom Size Modal UI Logic
  Premium implementation with localStorage persistence and Shopify line-item property integration.
*/

class CustomSizeModal {
  constructor() {
    this.modal = document.querySelector('.custom-size-modal');
    this.overlay = this.modal?.querySelector('.custom-size-modal__overlay');
    this.closeBtn = this.modal?.querySelector('.custom-size-modal__close');
    this.cancelBtn = this.modal?.querySelector('.custom-size-btn--secondary');
    this.saveBtn = this.modal?.querySelector('.custom-size-btn--primary');
    this.form = this.modal?.querySelector('form');
    this.productForm = document.querySelector('form[action*="/cart/add"]');
    this.storageKey = 'customSizeData';

    if (!this.modal) return;

    this.init();
  }

  init() {
    this.bindEvents();
    this.loadFromStorage();
    this.watchVariantChange();
    this.interceptAddToCart();
    this.initUnitToggle();
  }

  bindEvents() {
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    this.cancelBtn?.addEventListener('click', () => this.deselectCustomSize());
    
    this.form?.addEventListener('submit', (e) => this.handleSave(e));

    // Handle File Input label update
    this.form?.querySelector('input[type="file"]')?.addEventListener('change', (e) => {
      const fileName = e.target.files[0]?.name || 'Change Reference Image';
      const label = e.target.nextElementSibling;
      if (label) label.textContent = fileName;
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  initUnitToggle() {
    const units = this.form?.querySelectorAll('input[name="unit"]');
    units?.forEach(unit => {
      unit.addEventListener('change', (e) => {
        // Toggle placeholder text or suffixes if needed
        const unitName = e.target.value;
        this.form.querySelectorAll('.custom-size-field-flat input[type="number"]').forEach(input => {
          // Additional logic for unit conversion could go here in the future
        });
      });
    });
  }

  open() {
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.loadFromStorage();
  }

  close() {
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  deselectCustomSize() {
    this.close();
    // If user cancels, we should probably select a previous valid size or at least uncheck custom
    // But most users just want to close the modal.
    // If they clicked "Custom Size" and then "Cancel", we don't force a change, but they'll get validaton error on ATC.
  }

  handleSave(e) {
    if (e) e.preventDefault();
    
    if (!this.validateForm()) return;

    const formData = new FormData(this.form);
    const data = {};
    formData.forEach((value, key) => {
      if (key !== 'file') { // Skip direct file for localStorage
        data[key] = value;
      }
    });

    // Save to LocalStorage
    localStorage.setItem(this.storageKey, JSON.stringify(data));

    // Update Product Form Hidden Inputs
    this.updateProductFormProperties(data);

    this.close();
    
    // Reinforce the selection on the product page
    this.handleCustomSizeSelection(false);
  }

  validateForm() {
    let isValid = true;
    const requiredFields = this.form.querySelectorAll('input[required]');
    
    requiredFields.forEach(field => {
      if (!field.value.trim()) {
        isValid = false;
        field.parentElement.classList.add('has-error');
      } else {
        field.parentElement.classList.remove('has-error');
      }
    });

    // Body measurements check - at least some should be filled
    const measurements = this.form.querySelectorAll('.body-measurement-input');
    let filledCount = 0;
    measurements.forEach(m => { if(m.value.trim()) filledCount++; });

    if (filledCount === 0) {
      alert('Please fill in at least one body measurement.');
      isValid = false;
    }

    return isValid;
  }

  updateProductFormProperties(data) {
    const allForms = document.querySelectorAll('form[action*="/cart/add"]');
    if (allForms.length === 0) return;

    allForms.forEach(form => {
      // Remove existing custom size properties from this specific form
      form.querySelectorAll('input[name^="properties["]').forEach(input => {
         if (input.getAttribute('data-custom-property')) input.remove();
      });

      // Append new ones
      Object.entries(data).forEach(([key, value]) => {
        if (!value) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = value;
        input.setAttribute('data-custom-property', 'true');
        form.appendChild(input);
      });

      // Add explicit "Selected Size" property to clearly show in cart/admin
      const sizePropertyInput = document.createElement('input');
      sizePropertyInput.type = 'hidden';
      sizePropertyInput.name = 'properties[Customized]';
      sizePropertyInput.value = 'Yes (Measurements Provided)';
      sizePropertyInput.setAttribute('data-custom-property', 'true');
      form.appendChild(sizePropertyInput);
    });

    // Mark as "saved" for validation logic (checking any form)
    document.querySelector('form[action*="/cart/add"]').setAttribute('data-custom-size-saved', 'true');
  }

  loadFromStorage() {
    const saved = localStorage.getItem(this.storageKey);
    if (!saved) return;

    try {
      const data = JSON.parse(saved);
      if (this.form) {
        Object.entries(data).forEach(([key, value]) => {
          try {
            const input = this.form.querySelector(`[name="${key}"]`);
            if (input && input.type !== 'file') {
              input.value = value;
            }
          } catch (selectorErr) {
            // Guard against potential invalid characters in keys for selector engines
          }
        });
      }
      // Also update product form properties immediately so they are there if person clicks ATC
      this.updateProductFormProperties(data);
    } catch (e) {
      console.error('Error parsing custom size data', e);
    }
  }

  watchVariantChange() {
    // Listen for size selects. The standard Shopify theme uses change events on variant-selects or inputs.
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (target.name?.includes('options[Size]') || target.name?.includes('Size')) {
        if (target.value === 'Custom Size') {
          this.handleCustomSizeSelection(target);
        } else {
          // If another size is selected, clear custom selection states dynamically
          const customLabel = document.querySelector('label.is-custom-selected');
          if (customLabel) customLabel.classList.remove('is-custom-selected');
          
          const activeForm = document.querySelector('form[action*="/cart/add"]');
          activeForm?.removeAttribute('data-custom-size-saved'); // Reset for safety
        }
      }
    });

    // Handle radio buttons (pills) and their labels
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (target.tagName === 'INPUT' && target.type === 'radio' && target.value === 'Custom Size') {
        this.handleCustomSizeSelection(target);
        return;
      }

      // If clicked the label directly (or any element inside the label)
      const label = target.closest('.custom-size-label');
      if (label) {
        const inputId = label.getAttribute('for');
        const input = document.getElementById(inputId);
        this.handleCustomSizeSelection(input);
      }
    });
  }

  handleCustomSizeSelection(customRadio = null, shouldOpen = true) {
    if (this._handlingSelection) return;
    this._handlingSelection = true;

    try {
      if (shouldOpen) this.open();
      
      // Use the specific radio button element clicked, or fallback to first Custom Size radio in DOM
      const radio = customRadio || document.querySelector('input[value="Custom Size"]');
      if (radio) {
          const groupName = radio.name;
          const container = radio.closest('fieldset') || radio.closest('form') || document;
          
          // Uncheck everything else IN THE SIZE GROUP to stop theme conflict
          container.querySelectorAll(`input[name="${groupName}"]`).forEach(input => {
              input.checked = false;
              input.removeAttribute('checked');
              // Remove selection class from labels
              const label = container.querySelector(`label[for="${input.id}"]`);
              if (label) label.classList.remove('is-active', 'selected', 'is-selected'); 
          });

          // Check the custom radio
          radio.checked = true;
          radio.setAttribute('checked', 'checked');
          
          // Highlight custom label
          const customLabel = container.querySelector(`label[for="${radio.id}"]`);
          if (customLabel) customLabel.classList.add('is-custom-selected');

          // Trigger change event to sync with Shopify theme scripts
          radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      this.ensureAddToCartEnabled();
    } catch (err) {
      console.error('Error during custom size selection:', err);
    } finally {
      // Guaranteed to reset the selection flag so buttons never get stuck
      setTimeout(() => { this._handlingSelection = false; }, 200);
    }
  }

  ensureAddToCartEnabled() {
    // Keep trying for a second to override automated theme disables
    const timer = setInterval(() => {
      const atcButton = document.querySelector('button[name="add"]');
      if (atcButton) {
        atcButton.disabled = false;
        atcButton.classList.remove('disabled');
        const span = atcButton.querySelector('span:not(.loading-spinner)');
        if (span && (span.textContent.toLowerCase().includes('unavailable') || span.textContent.toLowerCase().includes('sold out'))) {
          span.textContent = 'Add to Cart';
        }
      }
    }, 200);

    setTimeout(() => {
      clearInterval(timer);
    }, 2000);
  }

  interceptAddToCart() {
    // Prevent add to cart if "Custom Size" is selected but not filled
    document.addEventListener('submit', (e) => {
      if (e.target.action?.includes('/cart/add')) {
        const selectedSize = this.getSelectedSize();
        if (selectedSize === 'Custom Size') {
          const activeForm = e.target;
          const isSaved = activeForm?.getAttribute('data-custom-size-saved') === 'true';
          if (!isSaved) {
            e.preventDefault();
            e.stopImmediatePropagation();
            alert('Please fill in your custom measurements before adding to cart.');
            this.open();
          }
        }
      }
    }, true);
  }

  getSelectedSize() {
    // Try to find the selected size variant value
    const sizeSelect = document.querySelector('select[name*="Size"], input[name*="Size"]:checked');
    return sizeSelect?.value;
  }
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  window.customSizeModal = new CustomSizeModal();
});
